import { Flex, Grid, GridItem, Box } from '@cad-arc';
import { Panel } from '../../panels/panel';
import { PanelTitle } from '../../panels/panel-title';
import { WorkspacePanel } from '../../panels/workspace-panel';
import { Conversations } from '../components/conversations-subpanel';
import { NewConversationCancellationModalProvider } from '../context/new-conversation-cancellation-modal-context';
import { useMessagingStore } from '../hooks/use-messaging-store';
import { useNewConversationCancellationModal } from '../hooks/use-new-conversation-cancellation-modal';
import { ConversationsFilterBar } from './conversations-filter-bar/filter-bar';
import { Messages } from './messages-subpanel';
import { NewConversation } from './new-conversation-subpanel';
import { useRef } from 'react';
import { EditorControl } from '../../common/components/lexical';
import { LoadingIndicator } from '../../common/components/loading-indicator';
import { useMessagingPanelHotkeys } from '../hooks/use-messaging-panel-hotkeys';
import { observer } from 'mobx-react';
import { ErrorBoundary } from '../../monitoring/error-boundary';
import { VerticalResizeSeparator, usePanelResizer } from '../hooks/usePanelResizer';
import {
    DEFAULT_CONVERSATION_LIST_WIDTH_PX,
    MINIMUM_CONVERSATION_LIST_WIDTH_PX,
    MINIMUM_MESSAGES_SUBPANEL_WIDTH_PX,
    PANEL_RESIZE_SEPARATOR_WIDTH,
} from '../constants';
import { MessageLoadFailBanner } from './message-load-fail-banner';
import { MessagingBookmarksButton } from './messaging-bookmarks-button';

type MessagingPanelBodyParams = {
    editorControlRef: React.MutableRefObject<EditorControl | undefined>;
    defaultFocusElementRef?: React.MutableRefObject<HTMLDivElement | null>;
    panelWidth: number;
};

const MessagingPanelBody = observer(
    ({
        editorControlRef,
        defaultFocusElementRef,
        panelWidth,
    }: MessagingPanelBodyParams): React.ReactElement => {
        const {
            isCreatingNewConversation,
            messagingBootstrapState,
            isBookmarksView,
        } = useMessagingStore();
        const { NewConversationCancellationModal } = useNewConversationCancellationModal();

        const containerRef = useRef<HTMLDivElement | null>(null);

        const { leftPanelWidth, separatorProps } = usePanelResizer({
            initialContainerWidth: panelWidth,
            initialLeftPanelWidth: DEFAULT_CONVERSATION_LIST_WIDTH_PX,
            minLeftPanelWidth: MINIMUM_CONVERSATION_LIST_WIDTH_PX,
            minRightPanelWidth: MINIMUM_MESSAGES_SUBPANEL_WIDTH_PX,
            containerRef,
            separatorWidth: PANEL_RESIZE_SEPARATOR_WIDTH,
        });
        return (
            <Flex flexDirection="column" w="100%" h="100%" ref={containerRef}>
                <MessageLoadFailBanner
                    testId="messaging-bootstrap-load-retry"
                    isError={messagingBootstrapState?.isError}
                    refetch={messagingBootstrapState?.refetch}
                />
                {messagingBootstrapState?.isFetching ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        <Flex>
                            <Box w="100%">
                                <ConversationsFilterBar
                                    defaultFocusElementRef={defaultFocusElementRef}
                                />
                            </Box>
                        </Flex>
                        <Flex h="100%">
                            <Grid
                                gridTemplateColumns={`${leftPanelWidth}px ${PANEL_RESIZE_SEPARATOR_WIDTH}px 1fr`}
                                h="100%"
                                w="100%"
                            >
                                <GridItem>
                                    <Conversations />
                                </GridItem>
                                <GridItem>
                                    <VerticalResizeSeparator
                                        data-testid="messaging-panel-separator"
                                        style={{
                                            borderRight:
                                                '1px solid var(--arc-colors-border-default)',
                                        }}
                                        {...separatorProps}
                                    />
                                </GridItem>
                                <GridItem>
                                    <Flex
                                        data-testid="messaging-right-panel"
                                        h="full"
                                        bg="surface.canvas"
                                        flexDirection="column"
                                    >
                                        {isCreatingNewConversation && (
                                            <NewConversation
                                                isBookmarksView={isBookmarksView}
                                                editorControlRef={editorControlRef}
                                            />
                                        )}
                                        {!isCreatingNewConversation && (
                                            <Messages editorControlRef={editorControlRef} />
                                        )}
                                    </Flex>
                                </GridItem>
                            </Grid>
                        </Flex>
                        <NewConversationCancellationModal />
                    </>
                )}
            </Flex>
        );
    }
);

type MessagingPanelParams = {
    panel: WorkspacePanel;
};

const MessagingPanelHeader = () => {
    return (
        <PanelTitle
            buttonBar={
                <Flex width="100%" alignItems="end" flexDir="column" className="flex">
                    <MessagingBookmarksButton />
                </Flex>
            }
        >
            Messages
        </PanelTitle>
    );
};

const WrappedPanel = observer(
    ({ panel }: MessagingPanelParams): React.ReactElement => {
        const editorControlRef = useRef<EditorControl>();
        const defaultFocusElementRef = useRef<HTMLDivElement | null>(null);
        const { keyDownHandler } = useMessagingPanelHotkeys({
            editorControlRef,
            defaultFocusElementRef,
        });
        return (
            <NewConversationCancellationModalProvider>
                <Panel
                    testId="messaging-panel"
                    onKeyDown={keyDownHandler}
                    tabIndex={-1}
                    panel={panel}
                    header={<MessagingPanelHeader />}
                    body={
                        <MessagingPanelBody
                            editorControlRef={editorControlRef}
                            defaultFocusElementRef={defaultFocusElementRef}
                            panelWidth={panel.width}
                        />
                    }
                    onBroughtToFront={() => {
                        setTimeout(() => {
                            // There's a timing issue causing editor focus() not to focus the editor when executed
                            // within the onBroughtToFront callback, which is resolved by setTimeout
                            editorControlRef.current?.focus();
                        }, 0);
                    }}
                />
            </NewConversationCancellationModalProvider>
        );
    }
);

export const MessagingPanel = ({ panel }: MessagingPanelParams): React.ReactElement => {
    return (
        <ErrorBoundary name="PANEL" messageVerbNoun="opening the messaging panel" asToast={true}>
            <WrappedPanel panel={panel} />
        </ErrorBoundary>
    );
};
